import { motion } from 'motion/react'
import { Leaf, Mail, Phone, ShieldCheck, Sparkles, Truck } from 'lucide-react'

import { categories } from '../../data/products'
import { ProductCard } from '../components/ProductCard'
import { TelegramOrderButton, WhatsAppIcon } from '../components/TelegramButton'
import { ButtonLink, Container, SectionTitle } from '../components/ui'
import { phoneUrl, useCatalog, whatsappUrl } from '../lib/catalog'
import { useI18n } from '../lib/i18n'

const heroShots = [
  { src: '/images/orik.jpg', alt: 'product_name_quritilgan_orik', className: 'left-0 top-[16%] aspect-[4/5] w-[48%] rotate-[-7deg]' },
  { src: '/images/anjir.jpg', alt: 'dried_figs', className: 'right-0 top-0 aspect-square w-[47%] rotate-[7deg]' },
  { src: '/images/xurmo.jpg', alt: 'category_xurmo', className: 'bottom-0 left-[28%] aspect-[4/5] w-[48%] rotate-[2deg]' },
]

const promises = [
  { icon: Leaf, title: 'feature_natural', body: 'feature_natural_text' },
  { icon: Sparkles, title: 'feature_selected', body: 'feature_selected_text' },
  { icon: ShieldCheck, title: 'feature_quality', body: 'feature_quality_text' },
  { icon: Truck, title: 'feature_service', body: 'feature_service_text' },
]

const steps = [
  { n: '01', title: 'step_choose', body: 'step_choose_text' },
  { n: '02', title: 'step_confirm', body: 'step_confirm_text' },
  { n: '03', title: 'step_delivery', body: 'step_delivery_text' },
]

export function Home() {
  const { t } = useI18n()
  const { featured, products, settings } = useCatalog()
  const popular = (featured.length ? featured : products).slice(0, 8)
  const whatsapp = settings.whatsapp ? whatsappUrl(settings.whatsapp) : null
  const phone = settings.phone ? phoneUrl(settings.phone) : null
  const email = settings.email ? `mailto:${settings.email}` : null

  return (
    <div className="pt-16">
      <section className="py-12 sm:py-16 md:py-20">
        <Container className="grid max-w-6xl items-center gap-8 px-4 sm:px-6 md:grid-cols-2 md:gap-10 lg:gap-16">
          <div className="order-1">
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="inline-flex rounded-full border border-line-strong bg-surface/70 px-4 py-2 text-[12px] font-medium uppercase tracking-[0.12em] text-forest"
            >
              {t('hero_eyebrow')}
            </motion.p>
            <h1 className="font-display mt-5 max-w-xl text-balance text-[clamp(42px,8vw,72px)] leading-[0.98]">
              {t('hero_title')}
            </h1>
            <p className="mt-5 max-w-lg text-pretty text-[15px] leading-relaxed text-muted sm:text-[17px]">
              {t('hero_description')}
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <ButtonLink to="/shop" className="min-h-12">{t('shop_now')}</ButtonLink>
            </div>
          </div>

          <div className="order-2 mx-auto aspect-[1.05] w-full max-w-[470px] md:aspect-square" aria-label={t('popular_products')}>
            <div className="relative size-full">
              {heroShots.map((shot, index) => (
                <motion.div
                  key={shot.src}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: index * 0.1 }}
                  className={`image-frame absolute overflow-hidden rounded-[24px] shadow-lift sm:rounded-[32px] ${shot.className}`}
                >
                  <img src={shot.src} alt={t(shot.alt)} loading="lazy" className="size-full object-cover" />
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section className="py-12 md:py-20">
        <Container className="max-w-6xl px-4 sm:px-6">
          <div className="grid auto-rows-fr gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {promises.map(({ icon: Icon, title, body }, index) => (
              <motion.article
                key={title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '0px 0px -5% 0px' }}
                transition={{ duration: 0.4, delay: index * 0.04 }}
                className="h-full rounded-[22px] border border-line bg-surface p-5 transition-colors hover:bg-forest-soft sm:p-6"
              >
                <span className="grid size-11 place-items-center rounded-[13px] bg-forest/10 text-forest">
                  <Icon className="size-5" />
                </span>
                <h2 className="mt-4 text-[15px] font-medium text-forest">{t(title)}</h2>
                <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{t(body)}</p>
              </motion.article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-12 md:py-20">
        <Container className="max-w-6xl px-4 sm:px-6">
          <SectionTitle title={t('popular_products')} action={<ButtonLink to="/shop" variant="secondary" className="min-h-11 !hidden sm:!inline-flex">{t('view_all')}</ButtonLink>} />
          <div className="mt-8 grid grid-cols-2 gap-x-3 gap-y-8 sm:gap-x-5 md:grid-cols-3 lg:grid-cols-4">
            {popular.map((product, index) => <ProductCard key={product.slug} product={product} index={index} />)}
          </div>
          <ButtonLink to="/shop" variant="secondary" className="mt-8 min-h-12 w-full sm:hidden">{t('view_all')}</ButtonLink>
        </Container>
      </section>

      <section id="kategoriyalar" className="scroll-mt-20 py-12 md:py-20">
        <Container className="max-w-6xl px-4 sm:px-6">
          <SectionTitle title={t('categories')} />
          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-4">
            {categories.map((category, index) => (
              <motion.a
                key={category.key}
                href={`/shop?kategoriya=${category.key}`}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (index % 4) * 0.04 }}
                className="group min-w-0 overflow-hidden rounded-[20px] border border-line bg-surface transition-shadow hover:shadow-lift"
              >
                <div className="image-frame aspect-[4/3] overflow-hidden">
                  <img src={category.image} alt={t(`category_${category.key}`)} loading="lazy" className="size-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-3 sm:p-4">
                  <h3 className="truncate text-[14px] font-medium text-forest sm:text-[16px]">{t(`category_${category.key}`)}</h3>
                  <p className="mt-1 line-clamp-2 text-[12px] leading-relaxed text-muted sm:text-[13px]">{t(`category_blurb_${category.key}`)}</p>
                </div>
              </motion.a>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-12 md:py-20">
        <Container className="max-w-6xl px-4 sm:px-6">
          <SectionTitle title={t('how_it_works')} />
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {steps.map((step) => (
              <article key={step.n} className="flex min-h-36 items-start gap-4 rounded-[20px] border border-line bg-surface p-5 sm:p-6">
                <span className="font-display text-[34px] leading-none text-forest">{step.n}</span>
                <div>
                  <h3 className="text-[15px] font-medium text-forest">{t(step.title)}</h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-muted">{t(step.body)}</p>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section id="aloqa" className="scroll-mt-20 py-12 md:py-20">
        <Container className="max-w-6xl px-4 sm:px-6">
          <div className="flex flex-col items-start justify-between gap-5 rounded-[26px] bg-forest p-6 text-bg sm:p-9 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <h2 className="font-display text-[clamp(30px,5vw,42px)] leading-tight">{t('contact_cta_title')}</h2>
              <p className="mt-2 text-[14px] leading-relaxed text-bg/80">{t('contact_cta_text')}</p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap">
              <TelegramOrderButton label={t('contact_us')} tone="light" size="lg" className="min-h-12 w-full sm:w-auto" />
              {phone && (
                <a href={phone} className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-[16px] border border-bg/35 px-5 text-[15px] font-medium text-bg transition-colors hover:bg-bg/10 sm:w-auto">
                  <Phone className="size-4 shrink-0" />
                  {settings.phone}
                </a>
              )}
              {email && (
                <a href={email} className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-[16px] border border-bg/35 px-5 text-[15px] font-medium text-bg transition-colors hover:bg-bg/10 sm:w-auto">
                  <Mail className="size-4 shrink-0" />
                  {settings.email}
                </a>
              )}
              {whatsapp && (
                <a href={whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-[16px] border border-bg/35 px-7 text-[16px] font-medium text-bg transition-colors hover:bg-bg/10 sm:w-auto">
                  <WhatsAppIcon className="size-4 shrink-0" />
                  {t('whatsapp_contact')}
                </a>
              )}
            </div>
          </div>
        </Container>
      </section>
    </div>
  )
}
